// ConflictsList (TASK-496) — ported from SixEase widgets/ConflictsList.tsx, re-grounded on
// Kielis portfolioFitService.getFit and re-tokenised. Conflicts = holdings scored 0 (an
// exclusion-tag hit). Kielis FitHolding lacks `issuer` (use security_name) and uses
// `market_value` (string|number) for current_chf. Provenance is the WidgetContainer badge;
// SixEase's DNA-sources footer is dropped (Kielis DnaResponse has no top-level sources).
import { AlertTriangle, Check } from 'lucide-react';
import { type FitHolding } from '../../../services/portfolioFitService';
import TagProvenanceChip from '../../portfolio/TagProvenanceChip';
import { useFit } from '../../../hooks/useFit';
import { WidgetContainer } from '../WidgetContainer';

const chfFormat = (v: number) =>
  new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(v);

function ConflictRow({ h }: { h: FitHolding }) {
  const exclusionTags = (h.conflicts ?? []).filter((c) => c.impact === 'exclusion').map((c) => c.tag);
  return (
    <div className="flex items-start gap-3 border-l-2 border-destructive py-1.5 pl-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold text-foreground">{h.security_name ?? '—'}</p>
        <p className="text-[11px] text-muted-foreground">{h.isin ?? '—'}</p>
        <div className="mt-1 flex flex-wrap gap-1">
          {/* TASK-827: the tag, and what it rests on. Every one is a sector default. */}
          {exclusionTags.map((tag) => (
            <TagProvenanceChip key={tag} tag={tag} provenance={h.tag_provenance?.[tag]} />
          ))}
        </div>
      </div>
      <span className="mt-0.5 shrink-0 font-mono text-[11px] text-muted-foreground">
        {chfFormat(Number(h.market_value ?? 0))}
      </span>
    </div>
  );
}

export function ConflictsList({ clientId }: { clientId: string }) {
  const { data, isLoading, isError, error } = useFit(clientId);

  return (
    <WidgetContainer source="Portfolio Fit">
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 w-full animate-pulse rounded bg-accent" />
          ))}
        </div>
      )}

      {isError && (
        <div className="text-[13px]">
          <p className="text-muted-foreground">Could not load conflicts.</p>
          <p className="mt-1 text-[11px] text-muted-foreground/70">
            {error instanceof Error ? error.message : String(error)}
          </p>
        </div>
      )}

      {data &&
        (() => {
          const conflicts = data.holdings.filter((h) => h.fit_score === 0);
          return (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <span className="text-[15px] font-semibold text-foreground">Conflicts</span>
                {conflicts.length > 0 ? (
                  <span className="inline-flex items-center gap-1 rounded border border-destructive/20 bg-destructive/10 px-2 py-0.5 text-[11px] font-semibold text-destructive">
                    <AlertTriangle className="h-3 w-3" />
                    {conflicts.length} holding{conflicts.length !== 1 ? 's' : ''}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded border border-emerald-500/20 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">
                    <Check className="h-3 w-3" />
                    None
                  </span>
                )}
              </div>

              {conflicts.length === 0 ? (
                <p className="text-[13px] text-emerald-600">
                  All holdings are DNA-compliant — no exclusion conflicts detected.
                </p>
              ) : (
                <div className="max-h-[400px] space-y-2 overflow-y-auto pr-0.5">
                  {conflicts.map((h) => (
                    <ConflictRow key={h.position_id} h={h} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
    </WidgetContainer>
  );
}
